/* eslint-disable react/jsx-boolean-value */
import React, { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { useIntl } from 'react-intl-next';
import {
	PopoverProvider,
	PopoverTarget,
	PopoverContent,
	SpotlightCard,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightBody,
	SpotlightFooter,
	SpotlightActions,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightStepCount,
} from '@atlaskit/spotlight';
import { Text, Box } from '@atlaskit/primitives';
import Image from '@atlaskit/image';
import stepOneImage from '../../assets/step-one.svg';
import stepTwoImage from '../../assets/step-two.svg';
import { messages } from '../../common/messages.tsx';
import { token } from '@atlaskit/tokens';
import { cssMap, css } from '@atlaskit/css';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { trackSpotlightEvent, trackErrorEvent } from '../../common/analytics.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

// Context for passing currentStep and isEnrolled to step wrappers
const SpotlightTourContext = createContext<{
	currentStep: number | null;
	isEnrolled: boolean;
}>({ currentStep: null, isEnrolled: false });

// Move cssMap to top-level scope
const SpotlightBodyStyles = cssMap({
	root: {
		marginInline: token('space.negative.200'),
	},
});

const imageStyles = css({
	width: '100%',
	height: 'auto',
});

// Generic step content component
type SpotlightStepProps = {
	currentStep: number;
	totalSteps: number;
	headlineMessage: typeof messages.stepOneHeadline;
	bodyMessage: typeof messages.stepOneBody;
	image: string;
	paddingInline?: 'space.100' | 'space.200';
	showBackButton?: boolean;
	primaryActionLabel: typeof messages.next | typeof messages.done;
	onBack?: () => void;
	footerOrder?: 'actions-first' | 'count-first';
};

const SpotlightStep = ({
	currentStep,
	totalSteps,
	headlineMessage,
	bodyMessage,
	image,
	paddingInline = 'space.200',
	showBackButton = false,
	primaryActionLabel,
	onBack,
	footerOrder = 'count-first',
}: SpotlightStepProps) => {
	const { formatMessage } = useIntl();

	return (
		<SpotlightCard>
			<SpotlightHeader>
				<SpotlightHeadline>{formatMessage(headlineMessage)}</SpotlightHeadline>
				<SpotlightControls>
					<SpotlightDismissControl />
				</SpotlightControls>
			</SpotlightHeader>
			<SpotlightBody>
				<Box xcss={SpotlightBodyStyles.root}>
					<Box paddingInline={paddingInline} paddingBlockEnd="space.100">
						<Text>{formatMessage(bodyMessage)}</Text>
					</Box>
					<Image src={image} alt="" css={imageStyles} />
				</Box>
			</SpotlightBody>
			<SpotlightFooter>
				{footerOrder === 'actions-first' ? (
					<>
						<SpotlightActions>
							{showBackButton && onBack && (
								<SpotlightSecondaryAction onClick={onBack}>
									{formatMessage(messages.back)}
								</SpotlightSecondaryAction>
							)}
							<SpotlightPrimaryAction>{formatMessage(primaryActionLabel)}</SpotlightPrimaryAction>
						</SpotlightActions>
						<SpotlightStepCount>
							{formatMessage(messages.stepCount, {
								current: currentStep + 1,
								total: totalSteps,
							})}
						</SpotlightStepCount>
					</>
				) : (
					<>
						<SpotlightStepCount>
							{formatMessage(messages.stepCount, {
								current: currentStep + 1,
								total: totalSteps,
							})}
						</SpotlightStepCount>
						<SpotlightActions>
							{showBackButton && onBack && (
								<SpotlightSecondaryAction onClick={onBack}>
									{formatMessage(messages.back)}
								</SpotlightSecondaryAction>
							)}
							<SpotlightPrimaryAction>{formatMessage(primaryActionLabel)}</SpotlightPrimaryAction>
						</SpotlightActions>
					</>
				)}
			</SpotlightFooter>
		</SpotlightCard>
	);
};

export const ExperimentExportJiraContextToPageNudgeSpotlightTour = ({
	children,
	shouldShowTour,
	isEnrolled = false,
	onTourComplete,
	onTourDismiss,
}: {
	children: ReactNode;
	shouldShowTour?: boolean;
	isEnrolled?: boolean;
	onTourComplete?: () => void;
	onTourDismiss?: () => void;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [currentStep, setCurrentStep] = useState<number | null>(
		shouldShowTour && isEnrolled ? 0 : null,
	);
	const totalSteps = 2; // Step 1 (page title) and Step 2 (share button)

	useEffect(() => {
		if (shouldShowTour && isEnrolled) {
			// Wait for one frame to ensure layout has settled and targets are in DOM
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setCurrentStep(0);
				});
			});
		} else {
			setCurrentStep(null);
		}
	}, [shouldShowTour, isEnrolled]);

	const next = () => {
		setCurrentStep((prev) => {
			if (prev === null) return 0;
			// Track next action with current step before moving forward
			trackSpotlightEvent({
				createAnalyticsEvent,
				action: 'next',
				step: prev,
			});
			const nextStep = prev + 1;
			if (nextStep >= totalSteps) {
				endTour(true);
				return null;
			}
			return nextStep;
		});
	};

	const back = () => {
		setCurrentStep((prev) => (prev === null ? 0 : Math.max(prev - 1, 0)));
	};

	const endTour = (isComplete: boolean) => {
		const stepToTrack = currentStep;
		setCurrentStep(null);
		// Track dismissal or completion
		if (isComplete) {
			trackSpotlightEvent({
				createAnalyticsEvent,
				action: 'completed',
				step: stepToTrack,
			});
			if (onTourComplete) {
				onTourComplete();
			}
		} else {
			trackSpotlightEvent({
				createAnalyticsEvent,
				action: 'dismissed',
				step: stepToTrack,
			});
			if (onTourDismiss) {
				onTourDismiss();
			}
		}
	};

	const done = () => endTour(true);
	const dismiss = () => endTour(false);

	// Handle errors and send operational event
	const handleError = useCallback(
		(error: Error, errorInfo: ErrorInfo) => {
			trackErrorEvent({
				createAnalyticsEvent,
				error,
				errorInfo,
				componentName: 'export-jira-context-nudge-spotlight-tour',
			});
		},
		[createAnalyticsEvent],
	);

	return (
		<GenericErrorBoundary
			attribution={Attribution.INTEGRATIONS}
			renderOnError={() => children}
			onError={handleError}
		>
			<SpotlightTourContext.Provider
				value={useMemo(() => ({ currentStep, isEnrolled }), [currentStep, isEnrolled])}
			>
				<PopoverProvider>
					{children}
					{/* Step 1 - Page Title */}
					{currentStep === 0 && (
						<PopoverContent isVisible={true} placement="left-end" dismiss={dismiss} done={next}>
							<SpotlightStep
								currentStep={currentStep}
								totalSteps={totalSteps}
								headlineMessage={messages.stepOneHeadline}
								bodyMessage={messages.stepOneBody}
								image={stepOneImage}
								showBackButton={false}
								primaryActionLabel={messages.next}
								onBack={undefined}
								footerOrder="count-first"
							/>
						</PopoverContent>
					)}
					{/* Step 2 - Share Button */}
					{currentStep === 1 && (
						<PopoverContent isVisible={true} placement="bottom-start" dismiss={dismiss} done={done}>
							<SpotlightStep
								currentStep={currentStep}
								totalSteps={totalSteps}
								headlineMessage={messages.stepTwoHeadline}
								bodyMessage={messages.stepTwoBody}
								image={stepTwoImage}
								showBackButton={true}
								primaryActionLabel={messages.done}
								onBack={back}
								footerOrder="count-first"
							/>
						</PopoverContent>
					)}
				</PopoverProvider>
			</SpotlightTourContext.Provider>
		</GenericErrorBoundary>
	);
};

export const ExperimentExportJiraContextToPageNudgeSpotlightStepWrapper = ({
	children,
	stepIndex,
}: {
	children: ReactNode;
	stepIndex: number;
}) => {
	const { currentStep, isEnrolled } = useContext(SpotlightTourContext);
	// Only render as PopoverTarget when this step is active and user is enrolled
	// PopoverProvider can only handle one PopoverTarget at a time
	const shouldRenderTarget = currentStep === stepIndex && isEnrolled;
	if (shouldRenderTarget) {
		return <PopoverTarget>{children}</PopoverTarget>;
	}
	// Otherwise just render children normally
	return <>{children}</>;
};
